import { createContext, useContext, useState, useCallback, useRef } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { toast } from 'sonner'
import * as api from '../api/fitcheck'

const TrashContext = createContext(null)

export function TrashProvider({ children }) {
  const { getToken } = useAuth()

  // Items in trash - synced with backend
  // Format: { id, type: 'wardrobe'|'outfit'|'generation', data: originalItem }
  const [trashedItems, setTrashedItems] = useState([])

  // Callbacks to restore items to their respective pages (set by pages)
  const restoreCallbacksRef = useRef({
    wardrobe: null,
    outfit: null,
    generation: null
  })

  // Timers for delayed operations
  const timersRef = useRef({})

  const setRestoreCallback = useCallback((type, callback) => {
    restoreCallbacksRef.current[type] = callback
  }, [])

  const softDelete = useCallback(async (type, item, onOptimisticDelete) => {
    const itemId = item.id
    const itemName = item.title || item.name || 'Item'

    // Optimistically remove from UI
    onOptimisticDelete?.(itemId)

    // Add to local trash state
    const trashedItem = {
      id: itemId,
      type,
      data: item
    }
    setTrashedItems(prev => [...prev, trashedItem])

    // Call API to soft delete (sets trashed_at on backend)
    try {
      const token = await getToken()

      if (type === 'wardrobe') {
        await api.deleteWardrobeItem(token, itemId)
      } else if (type === 'outfit') {
        await api.deleteOutfit(token, itemId)
      } else if (type === 'generation') {
        await api.deleteGeneration(token, itemId)
      }

      // Show toast with undo
      toast(`Moved to trash`, {
        description: itemName,
        action: {
          label: 'Undo',
          onClick: async () => {
            // Restore via API
            try {
              const token = await getToken()
              if (type === 'wardrobe') {
                await api.restoreWardrobeItem(token, itemId)
              } else if (type === 'outfit') {
                await api.restoreOutfit(token, itemId)
              } else if (type === 'generation') {
                await api.restoreGeneration(token, itemId)
              }

              // Remove from local trash
              setTrashedItems(prev => prev.filter(t => t.id !== itemId))

              // Restore to UI via callback
              const restoreCallback = restoreCallbacksRef.current[type]
              if (restoreCallback) {
                restoreCallback(item)
              }

              toast.success(`Restored ${itemName}`)
            } catch (err) {
              console.error('Failed to restore:', err)
              toast.error('Failed to restore')
            }
          }
        }
      })
    } catch (err) {
      console.error(`Failed to delete ${type}:`, err)
      toast.error(`Failed to delete ${itemName}`)

      // Restore on error
      setTrashedItems(prev => prev.filter(t => t.id !== itemId))
      const restoreCallback = restoreCallbacksRef.current[type]
      if (restoreCallback) {
        restoreCallback(item)
      }
    }
  }, [getToken])

  // Restore item from trash (manual restore from trash page)
  const restore = useCallback(async (trashedItem) => {
    const { id, type, data } = trashedItem

    try {
      const token = await getToken()

      if (type === 'wardrobe') {
        await api.restoreWardrobeItem(token, id)
      } else if (type === 'outfit') {
        await api.restoreOutfit(token, id)
      } else if (type === 'generation') {
        await api.restoreGeneration(token, id)
      }

      // Remove from local trash
      setTrashedItems(prev => prev.filter(t => t.id !== id))

      // Restore to UI
      const restoreCallback = restoreCallbacksRef.current[type]
      if (restoreCallback) {
        restoreCallback(data)
      }

      toast.success(`Restored ${data.title || data.name || 'Item'}`)
    } catch (err) {
      console.error('Failed to restore:', err)
      toast.error('Failed to restore')
    }
  }, [getToken])

  // Permanently delete from trash (no undo)
  const permanentDelete = useCallback(async (trashedItem) => {
    const { id, type, data } = trashedItem

    // Cancel any pending timer
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id])
      delete timersRef.current[id]
    }

    try {
      const token = await getToken()

      if (type === 'wardrobe') {
        await api.deleteWardrobeItem(token, id, { permanent: true })
      } else if (type === 'outfit') {
        await api.deleteOutfit(token, id, { permanent: true })
      } else if (type === 'generation') {
        await api.deleteGeneration(token, id, { permanent: true })
      }

      setTrashedItems(prev => prev.filter(t => t.id !== id))
      toast.success(`Permanently deleted ${data.title || data.name || 'Item'}`)
    } catch (err) {
      console.error(`Failed to delete ${type}:`, err)
      toast.error('Failed to delete')
    }
  }, [getToken])

  // Clear all items from trash (permanent delete all)
  const emptyTrash = useCallback(async () => {
    const items = [...trashedItems]

    // Cancel all timers
    Object.keys(timersRef.current).forEach(id => {
      clearTimeout(timersRef.current[id])
      delete timersRef.current[id]
    })

    // Delete all items
    const token = await getToken()
    const errors = []

    for (const item of items) {
      try {
        if (item.type === 'wardrobe') {
          await api.deleteWardrobeItem(token, item.id, { permanent: true })
        } else if (item.type === 'outfit') {
          await api.deleteOutfit(token, item.id, { permanent: true })
        } else if (item.type === 'generation') {
          await api.deleteGeneration(token, item.id, { permanent: true })
        }
      } catch {
        errors.push(item)
      }
    }

    setTrashedItems(errors)

    if (errors.length === 0) {
      toast.success('Trash emptied')
    } else {
      toast.error(`Failed to delete ${errors.length} items`)
    }
  }, [trashedItems, getToken])

  return (
    <TrashContext.Provider value={{
      trashedItems,
      softDelete,
      restore,
      permanentDelete,
      emptyTrash,
      setRestoreCallback
    }}>
      {children}
    </TrashContext.Provider>
  )
}

export function useTrash() {
  const context = useContext(TrashContext)
  if (!context) {
    throw new Error('useTrash must be used within TrashProvider')
  }
  return context
}
